import { makeStyles } from '@material-ui/core/styles';
import DatagridSearchTab from 'src/components/DatagridSearch/DatagridSearchTab';
import { SEARCH_TABS } from 'src/constants/SearchTabs';
import { useState } from 'react';
import { Collapse } from '@material-ui/core';
import DatagridQuickSearchContact from 'src/components/DatagridSearch/DatagridQuickSearchContact';
import DatagridAdvancedSearch from 'src/components/DatagridSearch/DatagridAdvancedSearch';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { t } from 'src/lib/language';
import { setFilters } from 'src/redux/actions/querybuilderActions';
import DatagridQuickSearchInvoice from 'src/components/DatagridSearch/DatagridQuickSearchInvoice';
import { PRESET_TYPES } from 'src/models/PresetModel';
import DatagridQuickSearchPayment from 'src/components/DatagridSearch/DatagridQuickSearchPayment';
import { useHotkeys } from 'react-hotkeys-hook';
import DatagridQuickSearchFundraisingTransaction
  from 'src/components/DatagridSearch/DatagridQuickSearchFundraisingTransaction';
import DatagridQuickSearchFundraising from './DatagridQuickSearchFundraising';
import DatagridQuickSearchTicket from './DatagridQuickSearchTicket';
import DatagridQuickSearchSubscriptions from './DatagridQuickSearchSubscriptions';

const useStyles = makeStyles(() => ({
  datagridSearch: {
    width: '100%',
  },
  datagridSearchTabs: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #eee',
  },
}));

interface Props {
  entity: ENTITIES,
  hideAdvancedSearch?: boolean,
}

const DatagridSearch = (props: Props) => {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const { entity, hideAdvancedSearch } = props;

  const lang = useSelector((state: Store) => state.language.language);

  const hasDefaultPreset = useSelector(
    (state: Store) => {
      const type = PRESET_TYPES.filter;
      if (!state.presets[entity]) return false;
      if (!state.presets[entity][type]) return false;
      return !!state.presets[entity][type].find((preset) => preset.is_default);
      return false;
    },
  );

  const getInitialTab = () => {
    if (hasDefaultPreset) {
      return SEARCH_TABS.advanced_search;
    }
    return SEARCH_TABS.quick_search;
  };

  const [selectedTab, setSelectedTab] = useState<SEARCH_TABS | null>(getInitialTab());
  const [opened, setOpened] = useState(true);

  const selectTab = (tab:SEARCH_TABS) => {
    dispatch(setFilters({
      entity,
      filters: null,
    }));
    if (tab === selectedTab) {
      setOpened(!opened);
      return;
    }
    setSelectedTab(tab);
    setOpened(true);
  };

  const closeTab = () => {
    setOpened(false);
  };

  useHotkeys('alt+q', () => selectTab(SEARCH_TABS.quick_search));
  useHotkeys('alt+a', () => selectTab(SEARCH_TABS.advanced_search));
  useHotkeys('alt+shift+c', () => setOpened(!opened));

  const getQuickSearch = () => {
    switch (entity) {
      case ENTITIES.contacts:
        return (<DatagridQuickSearchContact entity={entity} />);
      case ENTITIES.payments:
        return (<DatagridQuickSearchPayment entity={entity} />);
      case ENTITIES.fundraisings:
        return (<DatagridQuickSearchFundraising entity={entity} />);
      case ENTITIES.fundraisingTransactions:
        return (<DatagridQuickSearchFundraisingTransaction entity={entity} />);
      case ENTITIES.tickets:
        return (<DatagridQuickSearchTicket entity={entity} />);
      case ENTITIES.subscriptions:
        return (<DatagridQuickSearchSubscriptions entity={entity} />);
      case ENTITIES.invoices:
      default:
        return (<DatagridQuickSearchInvoice entity={entity} />);
    }
  };

  const renderSearchContent = () => {
    switch (selectedTab) {
      case SEARCH_TABS.quick_search:
        return getQuickSearch();
      case SEARCH_TABS.advanced_search:
        return (<DatagridAdvancedSearch closeTab={closeTab} entity={entity} />);
      default: return <></>;
    }
  };

  return (
    <div className={classes.datagridSearch}>
      <div className={classes.datagridSearchTabs}>
        <DatagridSearchTab
          id="quick-search-tab"
          text={t(lang, 'search.quickSearch')}
          first
          tab={SEARCH_TABS.quick_search}
          setSelected={selectTab}
          selected={hideAdvancedSearch || (selectedTab === SEARCH_TABS.quick_search)} />
        { !hideAdvancedSearch && (
          <DatagridSearchTab
            id="quick-advanced-tab"
            text={t(lang, 'search.advancedSearch')}
            tab={SEARCH_TABS.advanced_search}
            setSelected={selectTab}
            selected={selectedTab === SEARCH_TABS.advanced_search} />
        )}
      </div>
      <Collapse in={opened}>
        {renderSearchContent()}
      </Collapse>
    </div>
  );
};

export default DatagridSearch;
